"use client";

import React from 'react';
import ModelAutocomplete, { BrandAutocomplete } from '@/components/ModelAutocomplete';
import { Card, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { formatNumberInput } from '@/lib/utils'; // Importar la nueva utilidad

interface VehicleDetailsFormProps {
  details: Record<string, any>;
  setDetails: (details: Record<string, any>) => void;
  loading: boolean;
}

const VehicleDetailsForm: React.FC<VehicleDetailsFormProps> = ({ details, setDetails, loading }) => {
  const handleChange = (field: string, value: any) => {
    setDetails({ ...details, [field]: value });
  };

  const handleKilometersChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/[^0-9]/g, '');
    const numericValue = parseInt(rawValue, 10);
    
    // Almacenar el valor numérico (o null si está vacío)
    handleChange('kilometers', isNaN(numericValue) ? null : numericValue);
  };

  return (
    <Card className="p-4 border">
      <CardTitle className="text-lg mb-4">Detalles del Vehículo</CardTitle>
      <div className="grid grid-cols-1 gap-4">
        <div className="grid gap-2">
          <Label htmlFor="vehicle-brand">Marca</Label>
          <BrandAutocomplete
            value={details.brand || ''}
            onChange={(v) => handleChange('brand', v)}
            onSelect={(b) => {
              // when a brand is selected, set brand and clear model to encourage choosing a model from that brand
              handleChange('brand', b);
              handleChange('model', '');
            }}
            disabled={loading}
            placeholder="Empieza a escribir la marca (ej. Toyota)"
          />
        </div>

        <div className="grid gap-2">
          <Label htmlFor="vehicle-model">Modelo</Label>
          <ModelAutocomplete
            value={details.model || ''}
            onChange={(v) => handleChange('model', v)}
            onSelect={(v) => {
              // when a suggestion is selected, populate both brand and model (brand may override typed brand)
              if (v.brand) handleChange('brand', v.brand);
              handleChange('model', v.model);
            }}
            disabled={loading}
            brandFilter={details.brand || null}
            placeholder="Empieza a escribir el modelo (ej. Yaris)"
          />
        </div>

        <div className="grid gap-2">
          <Label htmlFor="vehicle-year">Año</Label>
          <Input
            id="vehicle-year"
            type="number"
            placeholder="2015"
            value={details.year || ''}
            onChange={(e) => handleChange('year', parseInt(e.target.value) || '')}
            disabled={loading}
          />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="vehicle-kilometers">Kilómetros</Label>
          <Input
            id="vehicle-kilometers"
            type="text" // Cambiado a text para permitir el formato
            inputMode="numeric"
            placeholder="120.000"
            value={formatNumberInput(details.kilometers)} // Usar la función de formato
            onChange={handleKilometersChange}
            disabled={loading}
          />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="vehicle-fuel-type">Tipo de Combustible</Label>
          <Select
            value={details.fuel_type || ''}
            onValueChange={(value) => handleChange('fuel_type', value)}
            disabled={loading}
          >
            <SelectTrigger id="vehicle-fuel-type"><SelectValue placeholder="Selecciona combustible" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="nafta">Nafta</SelectItem>
              <SelectItem value="diesel">Diésel</SelectItem>
              <SelectItem value="electrico">Eléctrico</SelectItem>
              <SelectItem value="hibrido">Híbrido</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-2">
          <Label htmlFor="vehicle-transmission">Transmisión</Label>
          <Select
            value={details.transmission || ''}
            onValueChange={(value) => handleChange('transmission', value)}
            disabled={loading}
          >
            <SelectTrigger id="vehicle-transmission"><SelectValue placeholder="Selecciona transmisión" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="manual">Manual</SelectItem>
              <SelectItem value="automatica">Automática</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-2">
          <Label htmlFor="vehicle-color">Color</Label>
          <Input
            id="vehicle-color"
            type="text"
            placeholder="Rojo"
            value={details.color || ''}
            onChange={(e) => handleChange('color', e.target.value)}
            disabled={loading}
          />
        </div>
        <div className="flex items-center space-x-2 col-span-full">
          <Checkbox
            id="vehicle-gnc"
            checked={!!details.gnc}
            onCheckedChange={(checked) => handleChange('gnc', checked)}
            disabled={loading}
          />
          <Label htmlFor="vehicle-gnc">Tiene GNC</Label>
        </div>
        <div className="grid gap-2">
          <Label htmlFor="vehicle-wheel-condition">Estado de Ruedas</Label>
          <Select
            value={details.wheel_condition || ''}
            onValueChange={(value) => handleChange('wheel_condition', value)}
            disabled={loading}
          >
            <SelectTrigger id="vehicle-wheel-condition"><SelectValue placeholder="Selecciona estado" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="excelente">Excelente</SelectItem>
              <SelectItem value="bueno">Bueno</SelectItem>
              <SelectItem value="regular">Regular</SelectItem>
              <SelectItem value="malo">Malo</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-2">
          <Label htmlFor="vehicle-engine-condition">Estado de Motor</Label>
          <Select
            value={details.engine_condition || ''}
            onValueChange={(value) => handleChange('engine_condition', value)}
            disabled={loading}
          >
            <SelectTrigger id="vehicle-engine-condition"><SelectValue placeholder="Selecciona estado" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="excelente">Excelente</SelectItem>
              <SelectItem value="bueno">Bueno</SelectItem>
              <SelectItem value="regular">Regular</SelectItem>
              <SelectItem value="malo">Malo</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-2">
          <Label htmlFor="vehicle-oil-condition">Estado de Aceite</Label>
          <Select
            value={details.oil_condition || ''}
            onValueChange={(value) => handleChange('oil_condition', value)}
            disabled={loading}
          >
            <SelectTrigger id="vehicle-oil-condition"><SelectValue placeholder="Selecciona estado" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="nuevo">Nuevo</SelectItem>
              <SelectItem value="bueno">Bueno</SelectItem>
              <SelectItem value="cambiar">Necesita cambio</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </Card>
  );
};

export default VehicleDetailsForm;